import { useState, useEffect, useCallback } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { IVehicleRotation } from 'interfaces/IWaybill';
import { routeService } from 'services/routeService';
import { dateToday } from 'utils/dateToday';
import debounce from 'lodash/debounce';

interface VehicleAutocompleteProps {
  value: IVehicleRotation | null;
  onChange: (vehicle: IVehicleRotation | null) => void;
  required?: boolean;
  error?: boolean;
  helperText?: string;
}

export function VehicleAutocomplete({ value, onChange, required = false, error = false, helperText }: VehicleAutocompleteProps) {
  const [vehicles, setVehicles] = useState<IVehicleRotation[]>([]);
  const [loading, setLoading] = useState(false);

  const searchVehicles = useCallback(
    debounce(async (search: string) => {
      try {
        setLoading(true);
        const response = await routeService.getVehicleRotation({
          search,
          pageSize: 10,
          date: dateToday()
        });
        setVehicles(response?.results || []);
      } catch (error) {
        console.error('Erro ao buscar veículos:', error);
        setVehicles([]);
      } finally {
        setLoading(false);
      }
    }, 500),
    []
  );

  // Carrega os primeiros 10 veículos ao montar o componente
  useEffect(() => {
    const loadInitialVehicles = async () => {
      try {
        setLoading(true);
        const response = await routeService.getVehicleRotation({ pageSize: 10, date: dateToday() });
        setVehicles(response?.results || []);
      } catch (error) {
        console.error('Erro ao carregar veículos:', error);
        setVehicles([]);
      } finally {
        setLoading(false);
      }
    };

    loadInitialVehicles();
  }, []);

  // Função para buscar veículos com base no termo de pesquisa
  const handleInputChange = async (_: any, newValue: string) => {
    if (newValue.length >= 2) {
      // Remove a placa do termo de busca (formato: "PLACA - NOME")
      const searchTerm = newValue.split(' - ').pop() || newValue;
      searchVehicles(searchTerm);
    } else if (newValue.length === 0 && !value) {
      // Recarrega os primeiros 10 veículos apenas se não houver veículo selecionado
      try {
        setLoading(true);
        const response = await routeService.getVehicleRotation({ pageSize: 10, date: dateToday() });
        setVehicles(response?.results || []);
      } catch (error) {
        console.error('Erro ao recarregar veículos:', error);
        setVehicles([]);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleChange = (_: any, newValue: IVehicleRotation | null) => {
    onChange(newValue);
    if (newValue) {
      setVehicles(prev => {
        const exists = prev.some(v => v.id === newValue.id);
        if (!exists) {
          return [newValue, ...prev];
        }
        return prev;
      });
    }
  };

  // Limpa o debounce quando o componente for desmontado
  useEffect(() => {
    return () => {
      searchVehicles.cancel();
    };
  }, [searchVehicles]);

  return (
    <Autocomplete
      options={vehicles || []}
      loading={loading}
      noOptionsText="Nenhum veículo encontrado"
      getOptionLabel={(option) =>
        option?.licensePlate
          ? `${option.licensePlate} - ${option.name || ''}`
          : option?.name || ''
      }
      value={value}
      onChange={handleChange}
      onInputChange={handleInputChange}
      filterOptions={(options) => options || []}
      renderInput={(params) => (
        <TextField
          {...params}
          label={required ? "Veículo *" : "Veículo"}
          error={error}
          helperText={helperText}
          size="medium"
          InputProps={{
            ...params.InputProps,
            sx: { height: 56, fontSize: 16 },
          }}
          inputProps={{
            ...params.inputProps,
            style: { fontSize: 16 },
          }}
        />
      )}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      size="medium"
    />
  );
}
